import {
  ActivityIndicator,
  StyleSheet,
  Text,
  View,
  type StyleProp,
  type ViewStyle,
} from "react-native";

import type { UiTheme } from "../theme";
import { Icon, type IconName } from "../Icon/Icon";

// <InlineStatusHint>: the one-line strip that says the numbers on screen are
// not the server's latest (CN-inline-status-hint, Antenore 25/09). Two
// callsites wrote the same row by hand: Progress/MuscleCoverageCompactSection (the
// "unconfirmed" banner) and Catalog/CatalogListView (the refresh hint). Same
// flex row, same small spinner on `ink3`, same muted caption.
//
// ⛔ The two tones are told apart by SILHOUETTE (spinner vs `!`), never by hue:
// the glyph is `ink2`, NOT `danger`. Was 4.20:1 (danger #d93025 on surface2
// #f3f0eb, brand/light, under the AA floor) when measured 2026-09-15; now
// 4.78:1 after CN-PALETTE-DANGERFILL (#cd2419). Glyph stays `ink2` regardless.
// Same remedy as DS-24. 📖 DS-89.
//
// ⛔ It announces itself. A reader who can't SEE it would take the numbers
// below as final, which is the bug it exists to prevent: assertive on a failed
// refresh (it interrupts, like CrashFallback), polite while one is in flight
// (it waits its turn, like ComposerSetRow). The glyph and the spinner are
// decoration and stay out of the tree, so the text is spoken as ONE sentence.

// #336: the program editor's save line is the third caller, and brings four
// tones of its own: where the edits are (server, device, queue) and whether a
// write is in flight. Same rule as the first two: each tone has its own
// SHAPE (✓ saved, pencil draft, clock queued, spinner saving), drawn in `ink2`
// like the `!`, so no tone is told apart by hue alone. These four are status,
// not alarms: they speak politely, never assertively.
export type InlineStatusHintTone =
  | "refreshing"
  | "error"
  | "saving"
  | "saved"
  | "draft"
  | "queued";

const TONE_ICON: Partial<Record<InlineStatusHintTone, IconName>> = {
  saved: "check",
  draft: "edit",
  queued: "clock",
};

export type InlineStatusHintProps = {
  theme: UiTheme;
  tone: InlineStatusHintTone;
  /** Shown and spoken: one string, so the eye and the ear can't drift apart. */
  text: string;
  /** Caption size. `sm` is the DS-16 floor. `xs` exists only because the
   *  Catalog hint shipped at it, and raising it is a design call (DS-87). */
  size?: "sm" | "xs";
  /** Placement only (margins, padding): the row itself is not negotiable. */
  style?: StyleProp<ViewStyle>;
  /** #336: the spoken name, when the shown text carries a detail that changes
   *  without news (a clock time rewritten on every draft write). Absent:
   *  `text`, as for the first two callers. */
  accessibilityText?: string;
  /** #336: false when the caller announces the changes itself (only when the
   *  tone changes). The row then stays a plain, readable node. */
  live?: boolean;
};

export function InlineStatusHint({
  theme,
  tone,
  text,
  size = "sm",
  style,
  accessibilityText,
  live = true,
}: InlineStatusHintProps) {
  const styles = makeStyles(theme);
  const fontSize = theme.typography.sizes[size];
  return (
    <View
      accessible
      accessibilityRole={tone === "error" ? "alert" : undefined}
      accessibilityLiveRegion={
        !live ? "none" : tone === "error" ? "assertive" : "polite"
      }
      accessibilityLabel={accessibilityText ?? text}
      style={[styles.row, style]}
    >
      {TONE_ICON[tone] ? (
        <View importantForAccessibility="no" accessibilityElementsHidden>
          <Icon name={TONE_ICON[tone]!} size={fontSize} color={theme.palette.ink2} />
        </View>
      ) : tone === "error" ? (
        <Text
          style={[styles.glyph, { fontSize }]}
          importantForAccessibility="no"
          accessibilityElementsHidden
        >
          !
        </Text>
      ) : (
        <ActivityIndicator
          size="small"
          color={theme.palette.ink3}
          importantForAccessibility="no"
          accessibilityElementsHidden
        />
      )}
      <Text style={[styles.text, { fontSize }]}>{text}</Text>
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s } = theme;
  return StyleSheet.create({
    row: { flexDirection: "row", alignItems: "center", gap: s.s2 },
    glyph: { color: p.ink2, fontWeight: "700" },
    text: { color: p.ink3, flexShrink: 1 },
  });
}
